/**
 * ParentLiveSummaryPage.jsx — Live class summary for parents.
 * Shows per-child live attendance, sessions covered, and a CTA into
 * the Parent Attendance Report premium tool inside the session detail.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import { parentLiveSummary, formatApiError } from "../../lib/liveClassApi";
import { toast } from "sonner";
import {
  Heart, Radio, Clock, CheckCircle2, ArrowRight, Sparkles,
} from "lucide-react";

export default function ParentLiveSummaryPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { setData(await parentLiveSummary()); }
      catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <DashboardLayout title="Child Live Classes">
      <div data-testid="lmslive-parent-summary" className="space-y-6 pb-12">
        <section className="relative overflow-hidden rounded-3xl border border-[#0F4C81] bg-gradient-to-br from-[#0A192F] via-[#0F4C81] to-[#0A192F] p-6 md:p-7 shadow-2xl text-white">
          <div className="absolute -top-32 -right-24 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-3xl" />
          <div className="relative">
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold flex items-center gap-2">
              <Heart size={12} /> Parent · Diplomexa Academy
            </p>
            <h1 className="mt-1 text-3xl md:text-4xl font-bold leading-tight">
              Your child&apos;s live classes
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-blue-100">
              Attendance, recorded sessions and parent reports — in one calm view.
            </p>
          </div>
        </section>

        {loading ? (
          <div className="grid h-48 place-items-center text-sm text-slate-500">Loading…</div>
        ) : !data?.children?.length ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
            No children linked yet. Link a child from &quot;Linked Children&quot;.
          </div>
        ) : (
          data.children.map((ch, i) => (
            <section key={ch.child?.id || i}
                      data-testid={`lmslive-parent-child-${ch.child?.id || i}`}
                      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37]">
                    {ch.child?.name || ch.child?.email}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {ch.sessions_attended} session(s) attended · {ch.total_live_minutes} live minutes
                  </p>
                </div>
                <span className="rounded-full bg-[#0A192F] px-2 py-0.5 text-[9px] font-bold uppercase text-[#D4AF37]">
                  Diplomexa Live
                </span>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
                {(ch.sessions || []).slice(0, 6).map(s => (
                  <Link key={s.id} to={`/dashboard/lms/live-sessions/${s.id}`}
                          data-testid={`lmslive-parent-session-${s.id}`}
                          className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 hover:border-[#D4AF37]/60">
                    <div className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-to-br from-[#0A192F] to-[#0F4C81] text-[#D4AF37]">
                      <Radio size={14} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-[#0A192F] truncate">{s.title}</p>
                      <p className="text-[10px] text-slate-500 inline-flex items-center gap-1">
                        <Clock size={10} /> {new Date(s.start_time).toLocaleString()} · {s.status}
                      </p>
                      <p className="mt-1 text-[10px] text-[#0F4C81] font-semibold inline-flex items-center gap-1">
                        Get parent report <Sparkles size={9} className="text-[#D4AF37]" />
                        <ArrowRight size={10} />
                      </p>
                    </div>
                  </Link>
                ))}
                {!ch.sessions?.length ? (
                  <p className="md:col-span-2 text-[11px] text-slate-500">
                    No live sessions for this child yet.
                  </p>
                ) : null}
              </div>
            </section>
          ))
        )}
      </div>
    </DashboardLayout>
  );
}
